/**
 * Format a content generation stage string into a human-readable label.
 *
 * Shared between ContentQueuePanel (blog list) and BlogDetailPage (detail view)
 * to ensure consistent progress display across the app.
 */
export const formatStageLabel = (stage?: string): string | null => {
  if (!stage) return null;

  // Parse section pattern: generating_section_{current}_of_{total}
  const sectionMatch = stage.match(/^generating_section_(\d+)_of_(\d+)$/i);
  if (sectionMatch) {
    const [, current, total] = sectionMatch;
    return `Section ${current}/${total}`;
  }

  // Parse block pattern: generating_{blockType}_{current}_of_{total}
  // Uses [\w-]+ to match hyphenated types like "ai-image"
  const match = stage.match(/^generating_([\w-]+)_(\d+)_of_(\d+)$/i);
  if (match) {
    const [, blockType, current, total] = match;
    const formatted = blockType
      .split('-')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
    return `Block ${current}/${total}: ${formatted}`;
  }

  // Map common stage names to friendly labels
  const stageLabels: Record<string, string> = {
    'queued': 'Queued - Waiting...',
    'initializing': 'Initializing...',
    'creating_strategy': 'Creating Content Strategy...',
    'planning_sections': 'Planning Sections...',
    'prefetching_research': 'Researching Topics...',
    'strategy': 'Creating Content Strategy',
    'strategy_and_headings': 'Planning & Headings',
    'processing_blocks': 'Processing Blocks',
    'generating_images': 'Generating Images',
    'assembling': 'Assembling Content',
    'running_post_processing': 'Post-Processing...',
    'completed': 'Completed',
    'failed': 'Failed',
  };

  if (stageLabels[stage]) {
    return stageLabels[stage];
  }

  // Fallback: format the stage nicely
  return stage
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
};
